import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import style from 'styled-components';

import { GIButton } from 'app-components';
import { useSelector } from 'react-redux';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { EnrollmentErrorCode } from '@constants/enrollmentList/enrollmentList.constant';
import { ENROLLMENT_STATUSES } from '@constants/individualPortal/dashboard/dashboardModelTypes';
import { getEnrollmentEditToolErrorMessage } from '../common/validation';
import { EnrollmentContext } from '../context/MemberMyEnrollmentProvider';
import useEnrollmentModal from '../hooks/useEnrollmentModal';

const StyledButtongroup = style.div`
    display: flex;
    gap: 1rem;
`;

const EnrollmentActionButtons = () => {
    const intl = useIntl();
    const permissions = useSelector((state: any) => state.memberEnrollment?.permissions);
    const { handleShowResult } = useEnrollmentModal();
    const { userPermissions } = permissions || {};
    const { isEdit, setIsEdit, enrollmentPremiumFormContext, setViewAddOrChangeTxn, selectedEnrollment } =
        useContext(EnrollmentContext);
    const {
        formState: { isDirty, isValid },
        watch,
        reset,
    } = enrollmentPremiumFormContext;
    const values = watch();

    const goToAddOrChangeControlEnrollmentPage = (isAdd: boolean) => {
        setViewAddOrChangeTxn(true);
    };

    const cancelPremiumUpdate = () => {
        setIsEdit(false);
        reset();
    };

    function validateAndSaveEnrollmentData() {
        let subscriberBenefitStartDates = '';
        let subscriberBenefitEndDates = '';
        let validationErrorCode = 0;
        let isValidDate = true;

        const enrollmentStartDate = new Date(values.enrollmentBenefitEffectiveStartDateUI);
        const enrollmentEndDate = new Date(values.enrollmentBenefitEffectiveEndDateUI);

        const isDifferentYear = (startDate, endDate) => startDate.getFullYear() !== endDate.getFullYear();
        const isBeforeStartDate = (startDate, checkDate) => checkDate < startDate;
        const isAfterEndDate = (endDate, checkDate) => checkDate > endDate;
        const isStartAfterEnd = (startDate, endDate) => startDate > endDate;

        for (const enrollee of values.enrolleeDataDtoList) {
            const enrolleeStartDate = new Date(enrollee.enrolleeEffectiveStartDateUI);
            const enrolleeEndDate = new Date(enrollee.enrolleeEffectiveEndDateUI);

            if (!isValidDate) break;

            if (isDifferentYear(enrollmentStartDate, enrollmentEndDate)) {
                isValidDate = false;
                validationErrorCode = EnrollmentErrorCode.DATES_NOT_WITHIN_COVERAGE_YEAR;
            } else if (isDifferentYear(enrolleeStartDate, enrolleeEndDate)) {
                isValidDate = false;
                validationErrorCode = EnrollmentErrorCode.DATES_NOT_WITHIN_COVERAGE_YEAR;
            } else if (isBeforeStartDate(enrollmentStartDate, enrollmentEndDate)) {
                isValidDate = false;
                validationErrorCode = EnrollmentErrorCode.ENROLLMENT_END_DATE_LESS_THAN_START;
            } else if (
                isBeforeStartDate(enrollmentStartDate, enrolleeStartDate) &&
                enrollee.enrolleeEffectiveStartDateUI !== enrollee.enrolleeEffectiveEndDateUI
            ) {
                isValidDate = false;
                validationErrorCode = EnrollmentErrorCode.MEMBER_DATES_OUTSIDE;
            } else if (
                isAfterEndDate(enrollmentEndDate, enrolleeEndDate) &&
                enrollee.enrolleeEffectiveStartDateUI !== enrollee.enrolleeEffectiveEndDateUI
            ) {
                isValidDate = false;
                validationErrorCode = EnrollmentErrorCode.MEMBER_DATES_OUTSIDE;
            } else if (
                isStartAfterEnd(enrolleeStartDate, enrolleeEndDate) &&
                enrollee.enrolleeEffectiveStartDateUI !== enrollee.enrolleeEffectiveEndDateUI
            ) {
                isValidDate = false;
                validationErrorCode = EnrollmentErrorCode.MEMBER_BENEFIT_END_BEFORE_START;
            }

            if (enrollee.relationshipToSubscriber.toUpperCase() === 'SELF') {
                subscriberBenefitStartDates = enrolleeStartDate.toString();
                subscriberBenefitEndDates = enrolleeEndDate.toString();
            }
        }

        if (validationErrorCode !== 0 && !isValidDate) {
            const message = getEnrollmentEditToolErrorMessage(validationErrorCode, '');
            handleShowResult({
                isSuccess: false,
                isError: true,
                message,
            });
            return;
        }

        const areDatesUnchanged =
            subscriberBenefitStartDates === enrollmentStartDate.toString() &&
            subscriberBenefitEndDates.toString() === enrollmentEndDate.toString();

        if (areDatesUnchanged) {
            goToAddOrChangeControlEnrollmentPage(false);
        }
    }

    return (
        <ConditionalRendering isTrue={isEdit}>
            <StyledButtongroup>
                <ConditionalRendering
                    isTrue={
                        userPermissions?.memberEnrollmentMrcEdit &&
                        selectedEnrollment.enrollmentStatus !== ENROLLMENT_STATUSES.CANCEL
                    }
                >
                    <GIButton
                        id="btn-MRC"
                        onClickHandler={() => goToAddOrChangeControlEnrollmentPage(true)}
                        variant="primary"
                        isDisabled={isDirty}
                        label={intl.formatMessage({ id: 'btn.enrollment.MRC' })}
                    />
                </ConditionalRendering>

                <GIButton
                    id="btn-continue"
                    onClickHandler={() => validateAndSaveEnrollmentData()}
                    variant="primary"
                    label={intl.formatMessage({ id: 'button.continue' })}
                    isDisabled={!(isDirty && isValid)}
                />
                <GIButton
                    id="btn-cancel"
                    onClickHandler={() => cancelPremiumUpdate()}
                    variant="secondary"
                    label={intl.formatMessage({ id: 'label.cancel' })}
                />
            </StyledButtongroup>
        </ConditionalRendering>
    );
};

export default EnrollmentActionButtons;
